import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "डेली हाउसकीपिंग",
  "डीप क्लीनिंग",
  "कारपेट / सोफा केयर",
  "फैसाड / ग्लास क्लीनिंग",
  "अन्य",
];

const inputCls =
  "w-full bg-[#0C1B24] border border-white/15 focus:border-[#00F0FF] outline-none px-4 py-3.5 font-mukta text-white placeholder:text-slate-500 transition-colors";

export const Booking = () => {
  const [form, setForm] = useState({ name: "", phone: "", service: "", address: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const whatsappText = encodeURIComponent(
    `नमस्ते, मैं ${form.name || "___"} हूँ। मुझे ${form.service || "क्लीनिंग सेवा"} की Enquiry करनी है। संपर्क: ${form.phone || "___"}`
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("कृपया नाम, फ़ोन नंबर और सेवा ज़रूर भरें।");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("धन्यवाद! आपकी enquiry दर्ज हो गई — हम जल्द संपर्क करेंगे।");
      setSubmitted(true);
    } catch {
      toast.error("कुछ गड़बड़ हुई — कृपया WhatsApp पर संपर्क करें।");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="cta-section" className="border-t border-white/10">
      <div className="py-24 md:py-32 bg-[#14242F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <SectionLabel>[ 06 — Enquiry / BOOK NOW ]</SectionLabel>
            <h2 className="mt-4 font-khand font-bold text-4xl sm:text-5xl leading-tight">
              आज ही <span className="text-[#00F0FF]">Enquiry</span> करें
            </h2>
            <p className="mt-4 text-slate-400 font-mukta text-base md:text-lg">
              अपनी ज़रूरत बताएं — हम जल्द से जल्द संपर्क करके कोटेशन देंगे।{" "}
              <span className="text-[#FFB300] font-semibold">जल्दी बुक करें — विशेष ऑफर उपलब्ध!</span>
            </p>

            <form data-testid="booking-form" onSubmit={handleSubmit} className="mt-10 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  data-testid="booking-name-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="आपका नाम *"
                  className={inputCls}
                />
                <input
                  data-testid="booking-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="फ़ोन नंबर *"
                  type="tel"
                  className={inputCls}
                />
              </div>

              <div>
                <p className="mb-3 font-spacemono text-[10px] tracking-[0.3em] text-slate-500 uppercase">
                  कौन-सी सेवा चाहिए? *
                </p>
                <div className="flex flex-wrap gap-2">
                  {serviceOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`service-option-${serviceOptions.indexOf(opt)}`}
                      onClick={() => setForm({ ...form, service: opt })}
                      className={`px-4 py-2.5 font-mukta text-sm border transition-colors ${
                        form.service === opt
                          ? "bg-[#00F0FF] text-[#0C1B24] border-[#00F0FF] font-semibold"
                          : "border-white/15 text-slate-300 hover:border-[#00F0FF] hover:text-[#00F0FF]"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <input
                data-testid="booking-address-input"
                value={form.address}
                onChange={set("address")}
                placeholder="साइट का पता (ऑफिस / बिल्डिंग)"
                className={inputCls}
              />
              <textarea
                data-testid="booking-message-input"
                value={form.message}
                onChange={set("message")}
                placeholder="कोई संदेश (वैकल्पिक)"
                rows={3}
                className={`${inputCls} resize-none`}
              />

              <div className="flex flex-wrap gap-4 pt-2">
                <button
                  data-testid="submit-booking-btn"
                  type="submit"
                  disabled={loading}
                  className="bg-[#00F0FF] text-[#0C1B24] font-mukta font-semibold px-8 py-4 flex items-center gap-3 hover:bg-white transition-colors disabled:opacity-60 shadow-[0_0_28px_rgba(0,240,255,0.3)]"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? "भेजा जा रहा है..." : "Enquiry भेजें"}
                </button>
                <a
                  data-testid="booking-whatsapp-btn"
                  href={`https://wa.me/918923917079?text=${whatsappText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`border font-mukta font-medium px-8 py-4 flex items-center gap-3 transition-colors ${
                    submitted
                      ? "border-[#34C759] text-[#34C759]"
                      : "border-white/25 text-white hover:border-[#00F0FF] hover:text-[#00F0FF]"
                  }`}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp पर भेजें
                </a>
              </div>
              {submitted && (
                <p data-testid="booking-success-note" className="text-sm text-[#34C759] font-mukta">
                  Enquiry save हो गई है — चाहें तो WhatsApp से भी तुरंत भेज सकते हैं।
                </p>
              )}
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:pl-8">
            <div id="contact" data-testid="contact-info" className="border border-white/10 bg-[#0C1B24] p-8 md:p-12 h-full flex flex-col justify-between gap-10">
              <div>
                <SectionLabel>[ संपर्क / CONTACT ]</SectionLabel>
                <h3 className="mt-4 font-khand font-bold text-3xl md:text-4xl">सीधे बात करें</h3>
              </div>
              <div className="space-y-6">
                <a data-testid="contact-phone-1" href="tel:+918923917079" className="flex items-center gap-4 group">
                  <Phone className="w-5 h-5 text-[#00F0FF]" />
                  <span className="font-mukta text-lg group-hover:text-[#00F0FF] transition-colors">+91 89239 17079</span>
                </a>
                <a data-testid="contact-phone-2" href="tel:+918218230020" className="flex items-center gap-4 group">
                  <Phone className="w-5 h-5 text-[#00F0FF]" />
                  <span className="font-mukta text-lg group-hover:text-[#00F0FF] transition-colors">+91 82182 30020</span>
                </a>
                <a data-testid="contact-whatsapp" href="https://wa.me/918923917079" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                  <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
                  <span className="font-mukta text-lg group-hover:text-[#00F0FF] transition-colors">WhatsApp — 89239 17079</span>
                </a>
                <a data-testid="contact-whatsapp-2" href="https://wa.me/918218230020" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                  <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
                  <span className="font-mukta text-lg group-hover:text-[#00F0FF] transition-colors">WhatsApp — 82182 30020</span>
                </a>
                <a data-testid="contact-email" href="mailto:ankitnkumarmznup@gmail.com" className="flex items-center gap-4 group">
                  <Mail className="w-5 h-5 text-[#00F0FF]" />
                  <span className="font-mukta text-lg break-all group-hover:text-[#00F0FF] transition-colors">
                    ankitnkumarmznup@gmail.com
                  </span>
                </a>
                <div data-testid="contact-address" className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#00F0FF] mt-1 shrink-0" />
                  <span className="font-mukta text-lg text-slate-300 leading-relaxed">
                    अमित विहार, सरस्वती विद्या मंदिर स्कूल के पास,
                    <br />
                    कुकरड़ा, नई मंडी, मुज़फ्फरनगर (उ.प्र.)
                  </span>
                </div>
              </div>
              <p className="font-spacemono text-[10px] tracking-[0.3em] text-slate-500 uppercase">
                Mon–Sun · 8 AM – 8 PM
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="bg-[#14242F] pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <div data-testid="map-card" className="border border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-white/10 bg-[#0C1B24]">
                <p className="font-spacemono text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase">
                  [ हमें ढूंढें / FIND US — कुकरड़ा, नई मंडी, मुज़फ्फरनगर ]
                </p>
                <a
                  data-testid="directions-link"
                  href="https://maps.google.com/?q=Saraswati+Vidya+Mandir+School,+Kukra,+New+Mandi,+Muzaffarnagar,+Uttar+Pradesh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mukta text-sm text-white hover:text-[#00F0FF] transition-colors"
                >
                  Directions लें →
                </a>
              </div>
              <iframe
                data-testid="map-iframe"
                title="Super Cleaner Services — Kukra, New Mandi, Muzaffarnagar"
                src="https://maps.google.com/maps?q=Saraswati%20Vidya%20Mandir%20School%2C%20Kukra%2C%20New%20Mandi%2C%20Muzaffarnagar%2C%20Uttar%20Pradesh&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-[380px] grayscale invert-[0.92] contrast-[0.9]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
